import React, { useMemo, useState, useEffect } from "react";
import { Box, Card, CardContent, Grid, Divider, TextField, CardActions, Tooltip, IconButton, Button, Typography, Stack } from "@mui/material";
import Chart from 'react-apexcharts';
import { useData } from "../../../context/DataContext";
import SingleSelectComponent from "../../../components/Selections/SingleSelectComponent";
import LoadingComponent from "../../../components/Loading/LoadingComponent";
import MultiSelectComponent from "../../../components/Selections/MultiSelectComponent"
import MaterialReactTableWithSearch from "../../../components/MaterialReactTable/MaterialReactTableWithSearch";
import SingleSelect from "../../../components/Selections/singleSelect";
import MultiSelectWithAll from "../../../components/Selections/multiSelectWithAll";


const AdOptionSummaryReport = () => {
    const { user = {}, adOptionSummaryReport = [], handleAdOptionSummary, years = [], subLoading, villageGroups = [] } = useData();

    const ffGroupData = villageGroups.filter((item) => item.level_id === 4);

    const groupData = ffGroupData[0]?.data?.filter(item => item.group_id !== "").map((item) => ({ value: item.group_id, label: item.Groups }));
    const villageListData = ffGroupData[0]?.data?.filter((item) => item.village_id !== "").map((item) => ({ value: item.village_id, label: item.village }));

    const [state, setState] = useState({
        selected_group_ids: [],
        selected_village_ids: [],
        selected_group_ids_error: "",
        villageDetails: [],
        groupDetails: []
    });

    const [yearId, setYearId] = useState(null);
    const [selectedChatType, setSelectedChatType] = useState(2);
    const [tableData, setTableData] = useState([]);


    const totalVillages = useMemo(() => adOptionSummaryReport?.[0]?.data?.length || 0, [adOptionSummaryReport]);
    const totalVillageFarmers = useMemo(() => adOptionSummaryReport?.[0]?.data?.reduce((acc, curr) => acc + curr.no_of_farmers, 0) || 0, [adOptionSummaryReport]);
    const completedVillageFarmersReport = useMemo(() => adOptionSummaryReport?.[0]?.data?.reduce((acc, curr) => acc + curr.fb_mapped, 0) || 0, [adOptionSummaryReport]);

    const pendingVillageFarmersReport = totalVillageFarmers - completedVillageFarmersReport;


    const totalGroups = useMemo(() => adOptionSummaryReport?.[1]?.data?.length || 0, [adOptionSummaryReport]);
    const totalGroupFarmers = useMemo(() => adOptionSummaryReport?.[1]?.data?.reduce((acc, curr) => acc + curr.no_of_farmers, 0) || 0, [adOptionSummaryReport]);
    const completedGroupFarmersReport = useMemo(() => adOptionSummaryReport?.[1]?.data?.reduce((acc, curr) => acc + curr.fb_mapped, 0) || 0, [adOptionSummaryReport]);
    const pendingGroupFarmersReport = totalGroupFarmers - completedGroupFarmersReport;

    const generateChartData = (series, labels) => {
        if (adOptionSummaryReport.length > 0) {
            const allZero = series.every(value => value === 0);

            if (allZero) {
                return {
                    series: [1],
                    options: {
                        chart: {
                            type: 'donut',
                        },
                        labels: ['No Data'],
                        colors: ['#989898'],
                        tooltip: {
                            enabled: false,
                        },
                        fill: {
                            type: 'gradient',
                        },
                    },
                };
            }

            return {
                series,
                options: {
                    chart: {
                        type: 'donut',
                    },
                    labels,
                    fill: {
                        type: 'gradient',
                    },
                },
            };
        }
    };

    // const villageData = generateChartData([0, villageReportCompleted, villageReportPending], [Total Villages (${totalVillages}), Completed (${villageReportCompleted}), Pending (${villageReportPending})]);
    const villageFarmersData = generateChartData([0, 0, completedVillageFarmersReport, pendingVillageFarmersReport], [`Total Villages (${totalVillages})`, `Total Farmers (${totalVillageFarmers})`, ` Completed (${completedVillageFarmersReport})`, `Pending (${pendingVillageFarmersReport})`]);
    const groupFarmersData = generateChartData([0, 0, completedGroupFarmersReport, pendingGroupFarmersReport], [`Total Groups (${totalGroups})`, `Total Farmers (${totalGroupFarmers})`, `Completed (${completedGroupFarmersReport})`, `Pending (${pendingGroupFarmersReport})`]);
    const soilSummaryColumns = useMemo(() => {
        let additionalColumns = [];

        if (selectedChatType === 2) {
            additionalColumns = [
                { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id' },
                { accessorKey: "village_name", header: "Village Name" },
                { accessorKey: "no_of_farmers", header: "Total Farmers" },
                { accessorKey: "fb_mapped", header: "Completed" },
                {
                    accessorFn: (row) => {
                        const noOfFarmers = row.no_of_farmers || 0; // Default to 0 if undefined
                        const farmerMapped = row.fb_mapped || 0; // Default to 0 if undefined
                        return noOfFarmers - farmerMapped; // Calculate pending count
                    },
                    header: "Pending"
                },];
        } else if (selectedChatType === 3) {
            additionalColumns = [
                { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id' },
                { accessorKey: "group_name", header: "Group Name" },
                { accessorKey: "no_of_farmers", header: "Total Farmers" },
                { accessorKey: "fb_mapped", header: "Completed" },
                {
                    accessorFn: (row) => {
                        const noOfFarmers = row.no_of_farmers || 0;
                        const farmerMapped = row.fb_mapped || 0;
                        return noOfFarmers - farmerMapped;
                    },
                    header: "Pending"
                },];
        }

        return additionalColumns;
    }, [selectedChatType]);


    const handleChatClick = (type) => {
        setSelectedChatType(type);
        if (type === 2) {
            setTableData(adOptionSummaryReport?.[0]?.data)
            setState(prevState => ({
                ...prevState,
                villageDetails: villageListData || [],
                selected_village_ids: villageListData || [],
                selected_group_ids: []
            }));
        } else if (type === 3) {
            setState(prevState => ({
                ...prevState,
                groupDetails: groupData || [],
                selected_group_ids: groupData || [],
                selected_village_ids: []
            }));
            setTableData(adOptionSummaryReport?.[1]?.data)
        }
    };


    const handleInputChange = (name, value) => {
        if (name === 'yearId') {
            setYearId(value);
            handleAdOptionSummary(value);
        }
    };

    useEffect(() => {
        setYearId(years[0]?.value);
        handleAdOptionSummary(years[0].value);
        if (user.level_id === 2) {
            setSelectedChatType(3)
        } else {
            setSelectedChatType(2)
        }
    }, [years]);

    useEffect(() => {
        if (adOptionSummaryReport.length > 0) {
            if (user.level_id === 2) {
                setTableData(adOptionSummaryReport?.[1]?.data)
                setState(prevState => ({
                    ...prevState,
                    groupDetails: groupData || [],
                    selected_group_ids: groupData || [],
                    selected_village_ids: []
                }));
            } else {
                setTableData(adOptionSummaryReport?.[0]?.data);
                setState(prevState => ({
                    ...prevState,
                    villageDetails: villageListData || [],
                    selected_village_ids: villageListData || [],
                    selected_group_ids: []
                }));
            }
        }
    }, [adOptionSummaryReport]);


    const handleMultiselectVillagechange = (name, selectedOption = []) => {
        if (selectedOption.length > 0) {
            const selectedVillageIds = selectedOption.map(option => option.value);

            const filteredTableData = adOptionSummaryReport[0]?.data?.filter(item =>
                selectedVillageIds.includes(item.village_id.toString())
            ) || [];

            setState(prevState => ({
                ...prevState,
                selected_village_ids: selectedOption,
                selected_group_ids_error: "",
            }));
            setTableData(filteredTableData)
        } else {
            setState(prevState => ({
                ...prevState,
                selected_village_ids: [],
                selected_group_ids_error: "",
            }));
            setTableData([])
        }
    };


    const handleMultiselectGroupchange = (name, options = []) => {
        if (options.length > 0) {
            const selectedGroupIds = options.map(option => option.value);
            const filteredTableData = adOptionSummaryReport[1]?.data?.filter(item =>
                selectedGroupIds.includes(item.ff_group_id.toString())
            ) || [];
            setState(prevState => ({
                ...prevState,
                selected_group_ids: options,
                selected_group_ids_error: "",
            }));
            setTableData(filteredTableData)
        } else {
            setState(prevState => ({
                ...prevState,
                selected_group_ids: [],
                selected_group_ids_error: "",
            }));
            setTableData([])
        }
    };

    if (subLoading) {
        return <LoadingComponent />;
    }

    const renderChartColumn = (type, chartData, title) => {
        if (adOptionSummaryReport.length > 0) {
            return (
                <Grid item size={{ xs: 12, md: 4 }} onClick={() => handleChatClick(type)}
                    sx={{
                        boxShadow: 3,
                        cursor: "pointer",
                        border: selectedChatType === type ? "2px solid #1D5E40" : "2px solid transparent",
                        borderRadius: 1,
                        textAlign: "center",
                        '&:hover': {
                            border: "2px solid #1D5E40"
                        }
                    }}
                >
                    <Chart options={chartData.options} series={chartData.series} type="donut" height={170} />
                    <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                        <Typography color="success" variant="bold">{title}</Typography>
                    </Box>
                </Grid>
            );
        }
    };

    return (
        <Box>
            <Divider sx={{ bgcolor: '#f5f1f1ff' }} />

            <Card elevation={6} sx={{ mt: 1 }}>
                <Grid container alignItems="center" mt={1} mx={1}>
                    <Grid item size={{ xs: 12, md: 8 }} textAlign="start">
                        <Typography variant="h5" textTransform="uppercase">
                            AdOption Report
                        </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }} display="flex" justifyContent="flex-end">
                        <SingleSelect
                            name="yearId"
                            label="Select Year"
                            options={years}
                            value={yearId}
                            onChange={handleInputChange}
                        />
                    </Grid>
                </Grid>
                <Divider sx={{ bgcolor: '#f5f1f1ff', my: 1 }} />


                <CardContent>
                    <Grid container spacing={1}>
                        {user.level_id === 1 || user.level_id === null ? (
                            <>
                                {renderChartColumn(2, villageFarmersData, "Villages")}
                                {user.level_id === null && renderChartColumn(3, groupFarmersData, "Groups")}
                            </>
                        ) : user.level_id === 2 ? (
                            renderChartColumn(3, groupFarmersData, "Groups")
                        ) : null}
                    </Grid>


                    <Grid container spacing={1}>
                        <Grid item size={{ xs: 12, md: 4 }} mt={2} mb={2}>
                            {
                                selectedChatType === 2 ? (
                                    <MultiSelectWithAll
                                        name="selected_village_ids"
                                        label="Select Villages"
                                        options={state.villageDetails}
                                        value={state.selected_village_ids || []}
                                        onChange={handleMultiselectVillagechange}
                                    />
                                ) : (
                                    <MultiSelectWithAll
                                        name="selected_group_ids"
                                        label="Select Groups"
                                        options={state.groupDetails}
                                        value={state.selected_group_ids || []}
                                        onChange={handleMultiselectGroupchange}
                                    />
                                )
                            }
                        </Grid>
                    </Grid>


                    <Grid container spacing={1}>
                        <Grid item size={{ xs: 12, md: 12 }} sx={{ boxShadow: 6 }}>
                            <MaterialReactTableWithSearch data={tableData} columns={soilSummaryColumns} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};
export default AdOptionSummaryReport;